import isFunction from 'lib/isFunction';
import axios from 'axios';

export const checkHttpStatus = (response) => {
    if (!response) {
        return false;
    }

    if (response.status >= 200 && response.status < 300) {
        return response;
    } else if (response.status === 401) {
        console.log('LOGOUT 401', window.location);
        if (!window.location.pathname.startsWith('/auth')) {
            window.location = '/#/auth/logout';
        }
    } else {
        if (response.data?.errors) {
            return response;
        }
        let error = new Error(response.statusText);
        error.response = response;
        throw error;
    }
};

export const parseJSON = (response) => {
    return response.data;
};

export const parseBlob = (response) => {
    let type = 'application/pdf';
    if (response.data && response.data.type === 'text/html') {
        type = 'text/html';
    }
    const file = new Blob([response.data], { type: type });

    //Build a URL from the file
    const fileURL = URL.createObjectURL(file);
    //Open the URL on new Window
    window.open(fileURL);
    response.url = fileURL;
    return response;
};

export const standardHeaders = (auth) => {
    let headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    };

    if (auth?.token) {
        headers['Authorization'] = 'Bearer ' + auth.token;
    }

    return headers;
};

const crudQuery = (parsePath, onReceive) => {
    return (auth, params, controller) => {
        return axios
            .get(`${parsePath(null, params, auth)}`, {
                params: params,
                headers: standardHeaders(auth),
                signal: controller.signal,
                validateStatus: function (status) {
                    return status >= 200 && status < 500;
                },
            })
            .then(checkHttpStatus)
            .then(parseJSON)
            .then(onReceive);
    };
};

const crudList = (parsePath, onReceive, shouldIncludeHistoricalIfNoResult) => {
    const doRequest = (auth, params, controller) => {
        return axios
            .get(`${parsePath(null, params, auth)}`, {
                params: params,
                headers: standardHeaders(auth),
                signal: controller.signal,
                validateStatus: function (status) {
                    return status >= 200 && status < 500;
                },
            })
            .catch(function (thrown) {
                if (axios.isCancel(thrown)) {
                    return false;
                } else {
                    // handle error
                }
            })
            .then(checkHttpStatus)
            .then(parseJSON)
            .then((resp) => {
                if (
                    shouldIncludeHistoricalIfNoResult &&
                    resp.count === 0 &&
                    params.includeHistorical !== true
                ) {
                    return doRequest(
                        auth,
                        Object.assign({}, params, { includeHistorical: true }),
                        controller
                    );
                }

                if (!resp) {
                    return false;
                }

                return {
                    ...resp,
                    data: resp.data ? resp.data.map((x) => onReceive(x)) : [],
                };
            });
    };

    return doRequest;
};

const crudGet = (parsePath, onReceive) => {
    return (id, auth, params) => {
        return axios
            .get(`${parsePath(id, params, auth)}/${id}`, {
                params: params,
                headers: standardHeaders(auth),
                validateStatus: function (status) {
                    return status >= 200 && status < 500;
                },
            })
            .then(checkHttpStatus)
            .then(parseJSON)
            .then(onReceive);
    };
};

const crudDelSingle = (parsePath) => {
    return (auth, params) => {
        return axios.delete(`${parsePath(null, params, auth)}`, {
            headers: standardHeaders(auth),
            validateStatus: function (status) {
                return status >= 200 && status < 500;
            },
        });
    };
};

const crudDel = (parsePath) => {
    return (id, data, auth, params) => {
        return axios.delete(`${parsePath(id, params, auth)}/${id}`, {
            params: params,
            data: data,
            headers: standardHeaders(auth),
            validateStatus: function (status) {
                return status >= 200 && status < 500;
            },
        });
    };
};

const crudPatch = (parsePath, onSend, onReceive) => {
    return (id, data, auth, params) => {
        return axios
            .patch(`${parsePath(id, params, auth)}/${id}`, onSend(data), {
                headers: standardHeaders(auth),
                validateStatus: function (status) {
                    return status >= 200 && status < 500;
                },
            })
            .then(checkHttpStatus)
            .then(parseJSON)
            .then(onReceive);
    };
};

const crudCreate = (parsePath, onSend, onReceive) => {
    return (data, auth, params) => {
        return axios
            .post(
                `${parsePath(null, Object.assign({}, params, data), auth)}`,
                onSend(data),
                {
                    headers: standardHeaders(auth),
                    validateStatus: function (status) {
                        return status >= 200 && status < 500;
                    },
                }
            )
            .then(checkHttpStatus)
            .then(parseJSON)
            .then(onReceive);
    };
};

const crudUpdate = (parsePath, onSend, onReceive) => {
    return (id, data, auth, params) => {
        if (id === null) {
            return axios
                .put(
                    `${parsePath(id, Object.assign({}, params, data), auth)}`,
                    onSend(data),
                    {
                        headers: standardHeaders(auth),
                        validateStatus: function (status) {
                            return status >= 200 && status < 500;
                        },
                    }
                )
                .then(checkHttpStatus)
                .then(parseJSON)
                .then(onReceive);
        }
        return axios
            .put(
                `${parsePath(id, Object.assign({}, params, data), auth)}/${id}`,
                onSend(data),
                {
                    headers: standardHeaders(auth),
                    validateStatus: function (status) {
                        return status >= 200 && status < 500;
                    },
                }
            )
            .then(checkHttpStatus)
            .then(parseJSON)
            .then(onReceive);
    };
};

const crudUpload = (parsePath) => {
    return (id, file, auth, params) => {
        const fd = new FormData();
        fd.append('id', id);
        fd.append('file', file);

        return axios
            .post(`${parsePath(null, params, auth)}/${id}/file`, fd, {
                headers: {
                    Authorization: 'Bearer ' + auth.token,
                },
                validateStatus: function (status) {
                    return status >= 200 && status < 500;
                },
            })
            .then(checkHttpStatus);
    };
};

const crudDownload = (parsePath, onSend, onReceive) => {
    return (data, auth, params) => {
        return axios
            .post(
                `${parsePath(null, Object.assign({}, params, data), auth)}`,
                onSend(data),
                {
                    headers: standardHeaders(auth),
                    responseType: 'blob',
                    validateStatus: function (status) {
                        return status >= 200 && status < 500;
                    },
                }
            )
            .then(checkHttpStatus)
            .then(parseBlob)
            .then(onReceive);
    };
};

const crudCancel = (controller) => {
    if (controller !== undefined && controller !== null) {
        controller.abort('Operation canceled by the user.');
    }
};

export const controller = () => {
    return new AbortController();
};

export default function rest(path, onReceive, onSend, additionalArgs) {
    const parsePath = (id, params, auth) => {
        let newPath = path;

        if (isFunction(newPath)) {
            newPath = newPath(id, params, auth && auth.token);
        }

        // newPath = config.apiBase + "/" + newPath;

        if (params !== undefined) {
            Object.keys(params).forEach((v, k) => {
                newPath = newPath.replace('{' + k + '}', v);
            });
        }

        return newPath;
    };

    return {
        get: crudGet(parsePath, onReceive),
        query: crudQuery(parsePath, onReceive),
        list: crudList(
            parsePath,
            onReceive,
            additionalArgs?.shouldIncludeHistoricalIfNoResult
        ),
        patch: crudPatch(parsePath, onSend, onReceive),
        create: crudCreate(parsePath, onSend, onReceive),
        update: crudUpdate(parsePath, onSend, onReceive),
        upload: crudUpload(parsePath, onSend, onReceive),
        download: crudDownload(parsePath, onSend, onReceive),
        del: crudDel(parsePath, onSend, onReceive),
        delSingle: crudDelSingle(parsePath, onSend, onReceive),
        cancel: crudCancel,
        controller: controller,
    };
}
